import React, { useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  Box,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AleaInput from '../../components/AleaInput/AleaInput.tsx';
import AleaTelephone from '../../components/AleaTelephone/AleaTelephone.tsx';
import AleaCheckIcon from '../../icons/AleaCheckIcon.tsx';

type TelephoneValue = { code: string; phone: string };

interface NewLivingAreaValues {
  livingAreaName: string;
  telephone: TelephoneValue;
}

interface AleaNewLivingAreaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (values: NewLivingAreaValues) => void;
  initialValues?: Partial<NewLivingAreaValues>;
}

const AleaNewLivingAreaModal: React.FC<AleaNewLivingAreaModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialValues,
}) => {
  const { t } = useTranslation();

  const form = useForm<NewLivingAreaValues>({
    defaultValues: {
      livingAreaName: '',
      telephone: { code: '+49', phone: '' },
    },
    mode: 'onChange',
  });

  const handleConfirm = form.handleSubmit((values) => {
    onConfirm(values);
  });

  // Broadcast open/close to allow header to drop below modal
  useEffect(() => {
    const event = new CustomEvent('alea-global-modal-open', { detail: isOpen });
    window.dispatchEvent(event);
    return () => {
      const closeEvent = new CustomEvent('alea-global-modal-open', { detail: false });
      window.dispatchEvent(closeEvent);
    };
  }, [isOpen]);

  // Reset form with initial values when modal opens/values change
  useEffect(() => {
    if (isOpen) {
      form.reset({
        livingAreaName: initialValues?.livingAreaName || '',
        telephone: initialValues?.telephone || { code: '+49', phone: '' },
      });
    }
  }, [isOpen, initialValues, form]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay zIndex={1500} />
      <ModalContent data-testid="new-living-area-modal" zIndex={1501} width="602px" maxW="602px" borderRadius="20px" bg="#F3F2F2">
        <ModalHeader
          textAlign="left"
          color="#000"
          fontSize="25px"
          fontWeight={500}
          pt="50px"
          pb="50px"
          px="50px"
        >
          {t('newLivingArea')}
        </ModalHeader>
        <ModalBody pt={0} px="50px" pb="50px">
          <Box>
            <Box mb="20px">
              <AleaInput
                label={t('general.wohnbereichsname')}
                value={form.watch('livingAreaName') || ''}
                onChange={(e) => form.setValue('livingAreaName', e.target.value)}
                register={form.register('livingAreaName', {
                  required: t('requiredField', { field: t('general.wohnbereichsname') }),
                  maxLength: {
                    value: 30,
                    message: t('maxCharacterLimit', { value: 30 }),
                  },
                })}
                error={form.formState.errors.livingAreaName}
              />
            </Box>
            <AleaTelephone
              label={t('telLandline')}
              value={form.watch('telephone')}
              onChange={(val) => form.setValue('telephone', val)}
              register={form.register('telephone.phone')}
              error={undefined}
            />
          </Box>
        </ModalBody>
        <ModalFooter pt={0} pb="50px" px="50px">
          <Flex justifyContent="flex-end" alignItems="center" width="100%">
            <Button
              data-testid="new-living-area-modal-cancel"
              variant="link"
              onClick={onClose}
              color="alea.500"
              textDecoration="underline"
              fontSize="18px"
              fontWeight={400}
            >
              {t('general.cancel')}
            </Button>
            <Button
              data-testid="new-living-area-modal-save"
              colorScheme="alea"
              variant="solid"
              onClick={handleConfirm}
              h="50px"
              w="100px"
              borderRadius="20px"
              px="0"
              display="flex"
              alignItems="center"
              justifyContent="center"
              aria-label={t('save')}
              ml="25px"
            >
              <AleaCheckIcon color="white" size={29} />
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AleaNewLivingAreaModal;


